export enum BloomCategory {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard',
}

export enum GermanBloomCategory {
  EASY = 'Einfach',
  MEDIUM = 'Mittel',
  HARD = 'Schwierig',
}

export enum KnowledgeDimension {
  FACTUAL_KNOWLEDGE = 'factual_knowledge',
  CONCEPTUAL_KNOWLEDGE = 'conceptual_knowledge',
  PROCEDURAL_KNOWLEDGE = 'procedural_knowledge',
}

export enum GermanKnowledgeDimension {
  FACTUAL_KNOWLEDGE = 'Faktenwissen',
  CONCEPTUAL_KNOWLEDGE = 'Konzeptionelles Wissen',
  PROCEDURAL_KNOWLEDGE = 'Prozedurales Wissen',
}

export enum WorksheetAiModel {
  GENERATIVE_AI = 'generative_ai',
  BLOOM_METHODOLOGY = 'bloom_methodology',
}

export enum GermanWorksheetAiModel {
  GENERATIVE_AI = 'Generative KI',
  BLOOM_METHODOLOGY = 'Bloom Methodology',
}

export enum WorksheetDistributionFilter {
  ALL = 'all',
  DRAFT = 'draft',
  SHARED = 'shared',
  DISTRIBUTED = 'distributed',
}

export enum ClassesTabFilter {
  ALL = 'all',
  CLASSES = 'classes',
  ORGANIZATION_CLASSES = 'organization_classes',
}

export enum WorksheetStudentType {
  STUDENT = 'STUDENT',
  GUEST = 'GUEST',
}
export enum WorksheetStudentsFilter {
  ALL = 'ALL',
  STUDENTS = WorksheetStudentType.STUDENT,
  GUESTS = WorksheetStudentType.GUEST,
}

export type WorksheetAiConfig = {
  model: WorksheetAiModel;
  variables?: {
    bloomCategory?: BloomCategory;
    knowledgeDimensions?: KnowledgeDimension[];
  };
  useSimpleLanguage?: boolean;
};

/**
 * Maps a technical enum value (BloomCategory, KnowledgeDimension, WorksheetAiModel) to its German label.
 * Returns the German label if found, otherwise returns null.
 */
export function toGermanLabel(value: string): string | null {
  // Try BloomCategory
  const bloomEntry = Object.entries(BloomCategory).find(
    ([key, val]) => val === value && key in GermanBloomCategory
  );
  if (bloomEntry) {
    const [key] = bloomEntry;
    return GermanBloomCategory[key as keyof typeof GermanBloomCategory];
  }
  // Try KnowledgeDimension
  const knowledgeEntry = Object.entries(KnowledgeDimension).find(
    ([key, val]) => val === value && key in GermanKnowledgeDimension
  );
  if (knowledgeEntry) {
    const [key] = knowledgeEntry;
    return GermanKnowledgeDimension[key as keyof typeof GermanKnowledgeDimension];
  }

  const modelEntry = Object.entries(WorksheetAiModel).find(
    ([key, val]) => val === value && key in GermanWorksheetAiModel
  );
  if (modelEntry) {
    const [key] = modelEntry;
    return GermanWorksheetAiModel[key as keyof typeof GermanWorksheetAiModel];
  }

  // Check for simple language
  if (value === 'simple_language') {
    return 'Einfache Sprache (DIN 8581-1)';
  }

  return null;
}

/**
 * Maps a technical enum value to its German label with a descriptive prefix for table display.
 * Returns a formatted string like "Bloom Kategorie: Mittel" or "Wissensdimensionen: Faktenwissen".
 */
export function toGermanTableLabel(value: string): string | null {
  // Try WorksheetAiModel first
  const modelEntry = Object.entries(WorksheetAiModel).find(
    ([key, val]) => val === value && key in GermanWorksheetAiModel
  );
  if (modelEntry) {
    const [key] = modelEntry;
    return `AI-Modell: ${GermanWorksheetAiModel[key as keyof typeof GermanWorksheetAiModel]}`;
  }

  // Try BloomCategory
  const bloomEntry = Object.entries(BloomCategory).find(
    ([key, val]) => val === value && key in GermanBloomCategory
  );
  if (bloomEntry) {
    const [key] = bloomEntry;
    return `Bloom Kategorie: ${GermanBloomCategory[key as keyof typeof GermanBloomCategory]}`;
  }
  // Try KnowledgeDimension
  const knowledgeEntry = Object.entries(KnowledgeDimension).find(
    ([key, val]) => val === value && key in GermanKnowledgeDimension
  );
  if (knowledgeEntry) {
    const [key] = knowledgeEntry;
    return `Wissensdimensionen: ${GermanKnowledgeDimension[key as keyof typeof GermanKnowledgeDimension]}`;
  }

  // Check for simple language
  if (value === 'simple_language') {
    return 'Sprachniveau: Einfache Sprache (DIN 8581-1)';
  }

  return null;
}
